import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import axios from "axios";
import {
  Menu,
  X,
  Phone,
  Mail,
  Box,
  Users,
  Headphones,
  ClipboardList,
  UserCheck,
  GraduationCap,
  ShieldCheck,
  Check,
  ArrowRight,
  Zap,
  Settings,
  FileText,
  CircleDollarSign,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageContext";
import { languages } from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

// Section wrapper with animation
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Language Switcher Component
const LanguageSwitcher = ({ isScrolled }) => {
  const { language, switchLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        data-testid="language-switcher"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
          isScrolled
            ? "text-text-secondary hover:bg-surface-light"
            : "text-white/90 hover:bg-white/10"
        }`}
      >
        <Globe size={18} />
        <span className="text-sm font-medium uppercase">{language}</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border border-border-subtle overflow-hidden z-50 min-w-[100px]">
            {languages.map((lang) => (
              <button
                key={lang.code}
                data-testid={`language-option-${lang.code}`}
                onClick={() => {
                  switchLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors flex items-center gap-2 ${
                  language === lang.code
                    ? "bg-red-50 text-brand-primary"
                    : "text-text-primary hover:bg-surface-light"
                }`}
              >
                <span className="uppercase">{lang.label}</span>
                <span className="text-text-secondary text-xs">
                  {lang.fullName}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Header Component
const Header = ({ onOpenQuote }) => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#szolgaltatasok", label: t.nav.services },
    { href: "#folyamat", label: t.nav.process },
    { href: "#rolunk", label: t.nav.about },
    { href: "#kapcsolat", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 glass-header border-b border-border-subtle"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a
            href="#"
            data-testid="logo"
            className="font-heading font-bold text-xl sm:text-2xl tracking-tight"
          >
            <span className={isScrolled ? "text-text-primary" : "text-white"}>
              AGM
            </span>
            <span className="text-brand-primary"> Logistics</span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-medium transition-colors hover:text-brand-primary ${
                  isScrolled ? "text-text-secondary" : "text-white/90"
                }`}
              >
                {link.label}
              </a>
            ))}
            <LanguageSwitcher isScrolled={isScrolled} />
            <button
              onClick={onOpenQuote}
              data-testid="header-cta-button"
              className="bg-brand-primary text-white font-medium px-5 py-2.5 rounded-lg hover:bg-brand-primary-hover transition-colors btn-press"
            >
              {t.nav.cta}
            </button>
          </nav>

          {/* Mobile: Language + Menu */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher isScrolled={isScrolled} />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 ${
                isScrolled ? "text-text-primary" : "text-white"
              }`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-border-subtle shadow-lg"
          >
            <nav className="flex flex-col p-4 gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-text-primary font-medium py-3 px-4 rounded-lg hover:bg-surface-light transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenQuote();
                }}
                data-testid="mobile-menu-cta-button"
                className="bg-brand-primary text-white font-medium py-3 px-4 rounded-lg hover:bg-brand-primary-hover transition-colors mt-2"
              >
                {t.nav.cta}
              </button>
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ onOpenQuote, onOpenCallback }) => {
  const { t } = useLanguage();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/221047/pexels-photo-221047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 py-32 sm:py-40">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p
            variants={fadeInUp}
            className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-6"
          >
            {t.hero.overline}
          </motion.p>

          <motion.h1
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
          >
            {t.hero.headline}{" "}
            <span className="text-brand-primary">{t.hero.headlineHighlight}</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="text-lg sm:text-xl text-white/80 mb-10 max-w-2xl"
          >
            {t.hero.subheadline}
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={onOpenQuote}
              data-testid="hero-cta-button"
              className="bg-brand-primary text-white font-medium px-8 py-4 rounded-lg hover:bg-brand-primary-hover transition-colors btn-press flex items-center justify-center gap-2"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={20} />
            </button>
            <button
              onClick={onOpenCallback}
              data-testid="hero-callback-button"
              className="bg-white/10 backdrop-blur-sm text-white font-medium px-8 py-4 rounded-lg hover:bg-white/20 transition-colors border border-white/30"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:block">
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2"
        >
          <div className="w-1.5 h-1.5 bg-white rounded-full" />
        </motion.div>
      </div>
    </section>
  );
};

// Social Proof Section
const SocialProofSection = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="social-proof-section" className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <AnimatedSection>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0">
            {t.socialProof.stats.map((stat, index) => (
              <div
                key={index}
                className="text-center stat-divider px-4 md:px-8"
              >
                <div className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-text-secondary text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const { t } = useLanguage();
  const icons = [Box, Users, Headphones];
  
  // Service images - index 0: Bérmunka, index 1: Munkaerő, index 2: Outsourcing
  const serviceImages = [
    {
      url: "/images/bermunka.jpg",
      alt: "Bérmunka - csomagok mozgatása futószalagon egy logisztikai üzemben",
    },
    null, // Munkaerő biztosítás - no image
    {
      url: "/images/outsourcing.jpg",
      alt: "Outsourcing és adminisztráció - irodai munkatárs számítógép előtt dolgozik",
    },
  ];

  return (
    <section
      id="szolgaltatasok"
      data-testid="services-section"
      className="py-24 sm:py-32 bg-surface-light"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <AnimatedSection className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-4">
            {t.services.overline}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary tracking-tight">
            {t.services.title}
          </h2>
        </AnimatedSection>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-6 sm:gap-8"
        >
          {t.services.items.map((service, index) => {
            const Icon = icons[index];
            const image = serviceImages[index];
            return (
              <motion.div
                key={index}
                variants={fadeInUp}
                data-testid={`service-card-${index + 1}`}
                className="bg-white border border-border-subtle rounded-2xl overflow-hidden card-hover"
              >
                {/* Service Image */}
                {image && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.alt}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>
                )}
                
                {/* Card Content */}
                <div className="p-8 sm:p-10">
                  <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-brand-primary" />
                  </div>
                  <h3 className="font-heading text-xl sm:text-2xl font-medium text-text-primary mb-3">
                    {service.title}
                  </h3>
                  <p className="text-text-secondary mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-text-secondary">
                        <Check className="w-5 h-5 text-brand-primary flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const { t } = useLanguage();
  const icons = [ClipboardList, UserCheck, GraduationCap, ShieldCheck];

  return (
    <section
      id="folyamat"
      data-testid="process-section"
      className="py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <AnimatedSection className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-4">
            {t.process.overline}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary tracking-tight">
            {t.process.title}
          </h2>
        </AnimatedSection>

        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden md:block absolute top-12 left-[12%] right-[12%] h-0.5 bg-border-subtle" />

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-4 gap-8 md:gap-6"
          >
            {t.process.steps.map((step, index) => {
              const Icon = icons[index];
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  data-testid={`process-step-${index + 1}`}
                  className="relative text-center md:text-center"
                >
                  {/* Step Circle */}
                  <div className="relative z-10 w-24 h-24 mx-auto bg-white border-2 border-border-subtle rounded-full flex items-center justify-center mb-6">
                    <Icon className="w-10 h-10 text-brand-primary" />
                  </div>
                  <div className="text-xs font-bold text-brand-primary mb-2">
                    {step.number}
                  </div>
                  <h3 className="font-heading text-lg font-medium text-text-primary mb-2">
                    {step.title}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const { t } = useLanguage();
  const icons = [Zap, Settings, FileText, CircleDollarSign];

  return (
    <section
      data-testid="why-choose-us-section"
      className="py-24 sm:py-32 bg-surface-light"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <AnimatedSection className="order-2 lg:order-1">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Modern, világos raktár raklapokkal és polcrendszerrel"
                loading="lazy"
                className="rounded-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-brand-primary text-white p-6 rounded-2xl hidden sm:block">
                <div className="font-heading text-3xl font-bold">10+</div>
                <div className="text-sm text-white/80">{t.whyUs.experienceBadge}</div>
              </div>
            </div>
          </AnimatedSection>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <AnimatedSection>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-4">
                {t.whyUs.overline}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary tracking-tight mb-8">
                {t.whyUs.title}
              </h2>
            </AnimatedSection>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="space-y-6"
            >
              {t.whyUs.benefits.map((benefit, index) => {
                const Icon = icons[index];
                return (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    data-testid={`benefit-${index + 1}`}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-white border border-border-subtle rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-brand-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-1">
                        {benefit.title}
                      </h3>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ onOpenQuote }) => {
  const { t } = useLanguage();

  return (
    <section data-testid="cta-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <AnimatedSection>
          <div className="bg-surface-dark text-white rounded-3xl p-12 md:p-24 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight">
              {t.cta.headline}{" "}
              <span className="text-brand-primary">{t.cta.headlineHighlight}</span>
            </h2>
            <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
              {t.cta.subheadline}
            </p>
            <button
              onClick={onOpenQuote}
              data-testid="cta-section-button"
              className="bg-brand-primary text-white font-medium px-10 py-4 rounded-lg hover:bg-brand-primary-hover transition-colors btn-press inline-flex items-center gap-2 text-lg"
            >
              {t.cta.button}
              <ArrowRight size={20} />
            </button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();

  return (
    <section
      id="rolunk"
      data-testid="about-section"
      className="py-24 sm:py-32 bg-surface-light"
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
        <AnimatedSection>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-4">
            {t.about.overline}
          </p>
          <blockquote className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-text-primary leading-snug tracking-tight">
            {t.about.quote}
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="w-12 h-0.5 bg-brand-primary" />
            <span className="text-text-secondary">{t.about.company}</span>
            <div className="w-12 h-0.5 bg-brand-primary" />
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ formData, setFormData, handleSubmit, isSubmitting }) => {
  const { t } = useLanguage();

  return (
    <section
      id="kapcsolat"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <AnimatedSection className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-4">
            {t.contact.overline}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary tracking-tight">
            {t.contact.title}
          </h2>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <AnimatedSection>
            <div className="space-y-8">
              <div>
                <h3 className="font-heading text-xl font-medium text-text-primary mb-4">
                  {t.contact.infoTitle}
                </h3>
                <p className="text-text-secondary mb-8 leading-relaxed">
                  {t.contact.infoDescription}
                </p>
              </div>

              <div className="space-y-4">
                <div
                  data-testid="contact-phone"
                  className="flex items-center gap-4 p-4 bg-surface-light rounded-xl"
                >
                  <div className="w-12 h-12 bg-white border border-border-subtle rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <div className="text-sm text-text-secondary">{t.contact.phone}</div>
                    <div className="font-medium text-text-primary select-text">
                      +36 30 228 0604
                    </div>
                  </div>
                </div>

                <div
                  data-testid="contact-email"
                  className="flex items-center gap-4 p-4 bg-surface-light rounded-xl"
                >
                  <div className="w-12 h-12 bg-white border border-border-subtle rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <div className="text-sm text-text-secondary">{t.contact.email}</div>
                    <div className="font-medium text-text-primary select-text">
                      office@agmlogistics.hu
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Contact Form */}
          <AnimatedSection>
            <form
              onSubmit={(e) => handleSubmit(e, "contact")}
              data-testid="contact-form"
              className="bg-surface-light rounded-2xl p-8"
            >
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-text-primary mb-2"
                  >
                    {t.contact.form.name} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    data-testid="contact-form-name"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-white border border-border-subtle rounded-lg input-focus focus:outline-none"
                    placeholder={t.contact.form.namePlaceholder}
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-text-primary mb-2"
                  >
                    {t.contact.form.phone} *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    data-testid="contact-form-phone"
                    required
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-white border border-border-subtle rounded-lg input-focus focus:outline-none"
                    placeholder={t.contact.form.phonePlaceholder}
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-text-primary mb-2"
                  >
                    {t.contact.form.message}
                  </label>
                  <textarea
                    id="message"
                    data-testid="contact-form-message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-white border border-border-subtle rounded-lg input-focus focus:outline-none resize-none"
                    placeholder={t.contact.form.messagePlaceholder}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-form-submit"
                  className="w-full bg-brand-primary text-white font-medium py-4 rounded-lg hover:bg-brand-primary-hover transition-colors btn-press disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t.contact.form.submitting : t.contact.form.submit}
                </button>
              </div>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Partner Badge Section
const PartnerBadgeSection = () => {
  return (
    <section data-testid="partner-badge-section" className="py-12 sm:py-16 bg-surface-light">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 flex justify-center">
        <span style={{ all: 'unset', display: 'inline-block', width: '200px', height: '200px', flex: '0 0 200px', aspectRatio: '1/1' }}>
          <span style={{ all: 'unset', display: 'inline-grid', justifyItems: 'center', justifyContent: 'center', alignContent: 'center', width: '100%', height: '100%', aspectRatio: '1/1' }}>
            <a href="https://autoline.hu/agmlogistiks-kft/" target="_blank" style={{ all: 'unset', gridArea: '1/1', overflow: 'hidden', cursor: 'pointer', borderRadius: '50%' }}>
              <img src="https://autoline.hu/ni/trusted-seller/1772433413960536344/badge.svg?project=1&locale=hu_hu&type=seller" alt="" style={{ all: 'unset', display: 'block', height: '100%', width: '100%', aspectRatio: '1 / 1' }} />
            </a>
            <a href="https://autoline.hu/agmlogistiks-kft/" target="_blank" style={{ all: 'unset', gridArea: '1/1', overflow: 'hidden', cursor: 'pointer', marginTop: '23%', height: '12.9%' }}>
              <img src="https://autoline.hu/img/svg/trusted-dealer/logo/autoline.svg" alt="" style={{ all: 'unset', display: 'block', height: '100%', width: '100%' }} />
            </a>
          </span>
        </span>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer data-testid="footer" className="bg-surface-dark text-white py-12">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="font-heading font-bold text-xl">
            AGM<span className="text-brand-primary"> Logistics</span>
          </div>
          <div className="text-white/60 text-sm">
            © {new Date().getFullYear()} AGM Logistics. {t.footer.rights}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Mobile Sticky CTA
const MobileStickyCTA = ({ onOpenQuote }) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div
      data-testid="mobile-sticky-cta"
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-border-subtle p-4 sticky-cta-shadow"
    >
      <button
        onClick={onOpenQuote}
        data-testid="mobile-sticky-cta-button"
        className="w-full bg-brand-primary text-white font-medium py-4 rounded-lg hover:bg-brand-primary-hover transition-colors btn-press"
      >
        {t.mobileCta}
      </button>
    </div>
  );
};

// Quote Modal
const QuoteModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  handleSubmit,
  isSubmitting,
  formType,
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const title = formType === "quote" ? t.modal.quote : t.modal.callback;

  return (
    <div
      data-testid="quote-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white rounded-2xl p-8 max-w-md w-full shadow-xl"
      >
        <button
          onClick={onClose}
          data-testid="quote-modal-close"
          className="absolute top-4 right-4 p-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          <X size={24} />
        </button>

        <h2 className="font-heading text-2xl font-semibold text-text-primary mb-2">
          {title}
        </h2>
        <p className="text-text-secondary mb-6">
          {t.modal.description}
        </p>

        <form onSubmit={(e) => handleSubmit(e, formType)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                {t.contact.form.name} *
              </label>
              <input
                type="text"
                required
                data-testid="quote-modal-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-3 bg-white border border-border-subtle rounded-lg input-focus focus:outline-none"
                placeholder={t.contact.form.namePlaceholder}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                {t.contact.form.phone} *
              </label>
              <input
                type="tel"
                required
                data-testid="quote-modal-phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="w-full px-4 py-3 bg-white border border-border-subtle rounded-lg input-focus focus:outline-none"
                placeholder={t.contact.form.phonePlaceholder}
              />
            </div>

            {formType === "quote" && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  {t.contact.form.message}
                </label>
                <textarea
                  rows={3}
                  data-testid="quote-modal-message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-white border border-border-subtle rounded-lg input-focus focus:outline-none resize-none"
                  placeholder={t.contact.form.messagePlaceholder}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="quote-modal-submit"
              className="w-full bg-brand-primary text-white font-medium py-4 rounded-lg hover:bg-brand-primary-hover transition-colors btn-press disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t.modal.submitting : t.modal.submit}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Main Home Page
const Home = () => {
  const { t } = useLanguage();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [modalFormType, setModalFormType] = useState("quote");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });

  const openQuoteModal = () => {
    setModalFormType("quote");
    setIsQuoteModalOpen(true);
  };

  const openCallbackModal = () => {
    setModalFormType("callback");
    setIsQuoteModalOpen(true);
  };

  const closeModal = () => {
    setIsQuoteModalOpen(false);
    setFormData({ name: "", phone: "", message: "" });
  };

  const handleSubmit = async (e, formType) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        phone: formData.phone,
        message: formData.message,
        form_type: formType,
      });

      toast.success(t.toast.success);
      setFormData({ name: "", phone: "", message: "" });
      if (isQuoteModalOpen) {
        closeModal();
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(t.toast.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header onOpenQuote={openQuoteModal} />
      <HeroSection
        onOpenQuote={openQuoteModal}
        onOpenCallback={openCallbackModal}
      />
      <SocialProofSection />
      <ServicesSection />
      <ProcessSection />
      <WhyChooseUsSection />
      <CTASection onOpenQuote={openQuoteModal} />
      <AboutSection />
      <ContactSection
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
      <PartnerBadgeSection />
      <Footer />
      <MobileStickyCTA onOpenQuote={openQuoteModal} />
      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={closeModal}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        formType={modalFormType}
      />
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
